import { resultGroups, callRules } from "../services/crm-v2-policy.mjs";
import CallOutcomeFields from "../../components/CallOutcomeFields";
import qualifications from "../../utils/qualifications";
import QuotationTemplates from "../../components/QuotationTemplates";
import React, { useEffect, useState } from "react";
import axios from "../../axios/AxiosInstance";
import { Field, Select, TextArea, Form } from "./ui";
import {
  todayParis,
  quoteValidityDate,
  addBusinessDays,
  isBusinessDay,
} from "../utils/calendar.mjs";
import {
  contractTypes,
  reasons,
  dissatisfactionCategories,
  lossReasons,
} from "../services/commercial.mjs";
const values = (fd) => Object.fromEntries(fd.entries());
export function MotifSelect({ label, name, options, required = true }) {
  const [selected, setSelected] = useState("");
  return <>
    <Select label={label} name={name} value={selected} onChange={e => setSelected(e.target.value)} required={required}>
      <option value="">Choisir</option>{options.map(value => <option key={value}>{value}</option>)}
    </Select>
    {selected === "Autre" && <TextArea label="Préciser le motif" name="otherReason" required />}
  </>;
}
export function FollowDate({
  today,
  closures = [],
  defaultDays = 30,
  name = "due",
}) {
  const [date, setDate] = useState(
    addBusinessDays(today, defaultDays, closures),
  );
  return (
    <>
      <p>Quand revenir ?</p>
      <div className="quick">
        {[1, 3, 15, 30, 60].map((days) => (
          <button
            key={days}
            type="button"
            className={
              date === addBusinessDays(today, days, closures) ? "selected" : ""
            }
            onClick={() => setDate(addBusinessDays(today, days, closures))}
          >
            {days} {days === 1 ? "jour ouvré" : "jours ouvrés"}
          </button>
        ))}
      </div>
      <Field
        label="Date personnalisée / prochaine action"
        name={name}
        type="date"
        value={date}
        min={today}
        required
        onChange={(e) => setDate(e.target.value)}
      />
    </>
  );
}
export function ContactForm({ contact, replace, duringCall, onSave }) {
  return (
    <Form
      onSubmit={(fd) =>
        onSave({
          ...values(fd),
          primary: fd.get("primary") === "on",
          active: fd.get("active") === "on",
          unavailableDays: fd.getAll("unavailableDays"),
        })
      }
    >
      <p className="muted">
        {replace ? "L’ancien contact sera conservé comme inactif." : ""}
        {duringCall
          ? " Après enregistrement, renseigne le résultat final du même appel."
          : ""}
      </p>
      <div className="formGrid">
        <Field
          label="Nom"
          name="lastName"
          defaultValue={contact?.lastName || ""}
          required
        />
        <Field
          label="Prénom"
          name="firstName"
          defaultValue={contact?.firstName || ""}
        />
      </div>
      <Field
        label="Fonction"
        name="role"
        list="contact-roles"
        defaultValue={contact?.role || ""}
      />
      <datalist id="contact-roles">
        {[
          "DRH",
          "Responsable RH",
          "Directeur",
          "Achats",
          "Cadre de santé",
          "Autre",
        ].map((role) => (
          <option key={role} value={role} />
        ))}
      </datalist>
      <div className="formGrid">
        <Field
          label="Téléphone direct"
          name="phone"
          type="tel"
          defaultValue={contact?.phone || ""}
        />
        <Field
          label="Email"
          name="email"
          type="email"
          defaultValue={contact?.email || ""}
        />
      </div>
      <Field
        label="Meilleur moment pour joindre"
        name="bestTime"
        defaultValue={contact?.bestTime || ""}
      />
      <fieldset>
        <legend>Jours d’indisponibilité</legend>
        <div className="taskFilters">
          {["Lundi", "Mardi", "Mercredi", "Jeudi", "Vendredi"].map((day) => (
            <label key={day} className="check">
              <input
                name="unavailableDays"
                type="checkbox"
                value={day}
                defaultChecked={contact?.unavailableDays?.includes(day)}
              />
              {day}
            </label>
          ))}
        </div>
      </fieldset>
      <label className="check">
        <input
          type="checkbox"
          name="primary"
          defaultChecked={replace || contact?.primary || false}
          disabled={replace}
        />
        {replace && <input type="hidden" name="primary" value="on" />}Contact
        principal
      </label>
      <label className="check">
        <input
          type="checkbox"
          name="active"
          defaultChecked={contact?.active !== false}
        />
        Contact actif
      </label>
      <Field
        label="Motif d’inactivité (si inactif)"
        name="inactiveReason"
        defaultValue={contact?.inactiveReason || ""}
      />
      <div className="notice">
        Si la fonction, le téléphone ou l’email manque lors d’un ajout, une
        tâche de complément sera créée au prochain jour ouvré.
      </div>
      <button className="primary wide">
        {duringCall
          ? "Enregistrer le contact puis continuer le même appel"
          : "Enregistrer le contact"}
      </button>
    </Form>
  );
}
export function NeedFields({ note = "", clientId, hasQuote = false, optional = false }) {
  const [quoteCheck, setQuoteCheck] = useState(null);
  const apiClient = /^[a-f0-9]{24}$/i.test(String(clientId || ""));
  useEffect(() => {
    if (!apiClient || hasQuote) return;
    let active = true;
    setQuoteCheck(null);
    axios.get(`quotation/by-client/${encodeURIComponent(clientId)}`)
      .then(({ data }) => {
        if (active && Array.isArray(data)) setQuoteCheck({ clientId, exists: data.length > 0 });
      })
      .catch(() => { /* Do not claim that no quote exists when lookup fails. */ });
    return () => { active = false; };
  }, [clientId, apiClient, hasQuote]);
  const showNotice = !hasQuote && (!apiClient || (quoteCheck?.clientId === clientId && !quoteCheck.exists));
  return (
    <>
      <div className="formGrid">
        <Field label="Métier" name="job" list="need-rome-jobs" required={!optional}
          onChange={event => event.target.setCustomValidity(qualifications.includes(event.target.value) ? '' : 'Choisissez un métier proposé.')} />
        <datalist id="need-rome-jobs">{qualifications.map(title => <option key={title} value={title} />)}</datalist>
        <Field
          label="Nombre de personnes"
          name="qty"
          type="number"
          min="1"
          step="1"
          defaultValue={optional ? "" : "1"}
          required={!optional}
        />
      </div>
      <Select label="Type de besoin" name="type">
        {optional && <option value="">À préciser</option>}
        {contractTypes.map((type) => (
          <option key={type}>{type}</option>
        ))}
      </Select>
      <div className="formGrid">
        <Field label="Date de démarrage souhaitée" name="start" type="date" />
        <Field label="Durée" name="duration" placeholder="Ex. 3 mois" />
        <Field label="Horaires" name="hours" placeholder="Ex. 7 h – 19 h" />
        <Select label="Urgence" name="urgency">
          <option>🔴 Immédiat</option>
          <option>🟠 Moins de 7 jours</option>
          <option>🟡 Moins de 30 jours</option>
          <option>🔵 Futur</option>
        </Select>
      </div>
      <TextArea label="Contraintes" name="constraints" />
      <TextArea label="Commentaires" name="notes" defaultValue={note} />
      {showNotice && <div className="notice">
        Le besoin reste commercial. Aucune transmission au recrutement avant
        signature du devis.
      </div>}
    </>
  );
}
export function CallResultForm({
  label,
  note,
  today,
  closures,
  onSave,
  saving = false,
  automationRules = {},
  noteTemplates = [],
  clientId,
  hasQuote = false,
}) {
  const [otherDecline, setOtherDecline] = useState(false);
  const [doNotRecall,setDoNotRecall]=useState(false);
  const [v2Details,setV2Details]=useState({});
  const rules=callRules({automationRules});
  return (
    <Form
      onSubmit={(fd) => {
        const data = values(fd);
        data.reasons = fd.getAll("reasons");
        data.deciderJoined = label !== "Agence d’intérim" && fd.get("deciderJoined") === "on";
        data.doNotRecall = fd.get("doNotRecall") === "on";
        data.managerTask = fd.get("managerTask") === "on";
        data.opposition = fd.get("opposition") === "on";
        data.qty = fd.get("qty") ? Number(fd.get("qty")) : null;
        if (!data.doNotRecall && data.due && !["À rappeler","Rendez-vous obtenu"].includes(label) && !isBusinessDay(data.due, closures))
          throw new Error("Choisis une prochaine action sur un jour ouvré.");

        onSave(data);
      }}
    >
      {["Nouveau besoin", "Demande de devis"].includes(label) ? (
        <NeedFields note={note} clientId={clientId} hasQuote={hasQuote} optional />
      ) : (
        <>
          {label === "Activité en baisse" && (
            <fieldset>
              <legend>Raisons de la baisse *</legend>
              {reasons.map((r) => (
                <label className="check" key={r}>
                  <input type="checkbox" name="reasons" value={r} onChange={e => r === "Autre" && setOtherDecline(e.target.checked)} />
                  {r}
                </label>
              ))}
              {otherDecline && <TextArea label="Préciser le motif" name="otherReason" required />}
            </fieldset>
          )}
          {label === "Travaille avec un concurrent" && (
            <>
              <Field label="Nom du concurrent" name="competitor" required />
              <Select label="Niveau de satisfaction" name="satisfaction">
                <option>Non connu</option>
                <option>Satisfait</option>
                <option>Mitigé</option>
                <option>Insatisfait</option>
              </Select>
              <Field
                label="Date de renouvellement / référencement connue"
                name="renewal"
                type="date"
              />
            </>
          )}
          {label === "Insatisfaction" && <MotifSelect label="Catégorie d’insatisfaction" name="category" options={dissatisfactionCategories} />}
          {label === "Client perdu" && <MotifSelect label="Motif de perte" name="lossReason" options={lossReasons} />}
          <TextArea label="Commentaire" name="notes" defaultValue={note} />
        </>
      )}
      {noteTemplates.length>0&&<label>Note modèle<select defaultValue="" onChange={event=>{const form=event.target.closest('form');const notes=form?.querySelector('[name="notes"]');if(notes)notes.value=event.target.value;}}><option value="">Choisir une note</option>{noteTemplates.map(text=><option key={text} value={text}>{text}</option>)}</select></label>}
      <label className="check"><input type="checkbox" name="doNotRecall" checked={doNotRecall} onChange={e=>setDoNotRecall(e.target.checked)} />Ne plus rappeler — retirer des leads</label>
      <p className="hint">La fiche et l’historique sont conservés. Les relances seront annulées et ce client ne sera plus proposé en prospection.</p>
      <CallOutcomeFields omitFollowup={doNotRecall} label={label} values={v2Details} onChange={(name,value)=>setV2Details(current=>({...current,[name]:value}))}/>
      {!doNotRecall && !resultGroups["Exclusion / situation entreprise"].includes(label) && !["Contact parti","Mauvais numéro","Numéro hors service","Email invalide","Rendez-vous obtenu"].includes(label) && (<FollowDate
        today={today}
        closures={closures}
        defaultDays={label === "Nouveau besoin" ? 1 : label === "Activité en baisse" ? rules.decliningDays : ["Devis envoyé","À rappeler","Pas de réponse","Pas de réponse / Non joignable"].includes(label) ? (label === "Devis envoyé" ? rules.quoteDays : rules.unansweredDays) : label === "Plaquette envoyée" ? rules.brochureDays : rules.noNeedDays}
      />)}
      {label !== "Agence d’intérim" && <label className="check">
        <input type="checkbox" name="deciderJoined" />
        Décideur joint pendant cet appel
      </label>}
      {label === "Agence d’intérim" && <p className="hint">Ce client sera exclu de la prospection. Aucun rappel ne sera créé et ses relances en cours seront annulées.</p>}

      {label === "Insatisfaction" && (
        <p className="notice">
          Une alerte client à risque sera visible dans le pilotage hyperadmin.
        </p>
      )}
      <button className="primary wide" disabled={saving}>
        {saving ? "Enregistrement…" : "Valider le résultat de mon appel"}
      </button>
    </Form>
  );
}
export function TaskForm({ state, clientId, onSave }) {
  const today = todayParis();
  return (
    <Form
      onSubmit={(fd) => {
        const data = values(fd);
        const c = state.clients.find((c) => String(c.id) === data.clientId);
        if (!isBusinessDay(data.due, state.calendar.closures))
          throw new Error("Choisis un jour ouvré.");
        onSave(c.id, data);
      }}
    >
      <Select label="Établissement" name="clientId" defaultValue={clientId}>
        {state.clients.map((c) => (
          <option key={c.id} value={c.id}>
            {c.name}
          </option>
        ))}
      </Select>
      <Field label="Objet" name="title" required />
      <Select label="Type" name="category">
        <option value="call">Saisie de résultat d’appel</option>
        <option value="follow">Relance</option>
        <option value="appointment">Rendez-vous</option>
        <option value="email">Email</option>
      </Select>
      <Field
        label="Échéance"
        name="due"
        type="date"
        min={today}
        defaultValue={addBusinessDays(today, 0, state.calendar.closures)}
        required
      />
      <Field label="Heure" name="time" type="time" />
      <TextArea label="Commentaire" name="notes" />
      <button className="primary wide">Créer la tâche</button>
    </Form>
  );
}
export function QuoteForm({ need, onSave }) {
  const [fields, setFields] = useState({ rate: '', coefficient: '', amount: '', conditions: '', notes: '' });
  const changeField = event => setFields(current => ({ ...current, [event.target.name]: event.target.value }));
  return (
    <Form
      onSubmit={(fd) =>
        onSave({
          ...values(fd),
          rate: Number(fd.get("rate")) || null,
          coefficient: Number(fd.get("coefficient")) || null,
          amount: Number(fd.get("amount")) || null,
        })
      }
    >
      <QuotationTemplates kind="commercial" getContent={() => fields} onApply={content => setFields({ rate: '', coefficient: '', amount: '', conditions: '', notes: '', ...content })} />
      <p>
        {need.job} × {need.qty} · {need.type}
      </p>
      <div className="formGrid">
        <Field
          label="Coefficient"
          name="coefficient" value={fields.coefficient} onChange={changeField}
          type="number"
          min="0"
          step="0.01"
        />
        <Field label="Taux (€)" name="rate" value={fields.rate} onChange={changeField} type="number" min="0" step="0.01" />
        <Field
          label="Montant estimé (€), optionnel"
          name="amount" value={fields.amount} onChange={changeField}
          type="number"
          min="0"
          step="0.01"
        />
        <Field
          label="Validité jusqu’au"
          name="validUntil"
          type="date"
          value={quoteValidityDate()}
          readOnly
        />
      </div>
      <TextArea label="Conditions" name="conditions" value={fields.conditions} onChange={changeField} required />
      <TextArea label="Commentaires" name="notes" value={fields.notes} onChange={changeField} />
      <button className="primary wide">Créer cette version du devis</button>
    </Form>
  );
}
